import { useState, useCallback } from 'react';
import tripFolderService from '../../../services/trips/trip-folder.service';
import { useTrip } from '../../contexts/trip-context';
import { ITripFolder } from '../../../Models/trips/trip-folder.model';

export interface IDeleteFolderModel {
  folderId: string;
  title: string;
  tripId: string;
}

export const useDeleteFolder = (
  folder: ITripFolder | null,
  onSuccess?: () => void
) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trip, setRefreshTrip } = useTrip();

  const deleteFolder = useCallback(async () => {
    if (!folder || !folder.id) {
      setError('Folder not found');
      return false;
    }

    const destinationId = folder.id || trip?.id;
    if (!destinationId) {
      setError('Destination ID not found');
      return false;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await tripFolderService.deleteFolder(destinationId, folder.id);

      // Refresh the trip data to update the UI
      setRefreshTrip(true);

      // Call success callback
      onSuccess?.();

      return true;
    } catch (error: any) {
      console.error('Error deleting folder:', error);
      const errorMessage =
        error?.message || 'Failed to delete folder. Please try again.';
      setError(errorMessage);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [folder, trip?.id, setRefreshTrip, onSuccess]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const hasChildren = useCallback(() => {
    if (!folder) return false;
    const hasChildFolders = folder.children && folder.children.length > 0;
    const hasActivities = folder.activities && folder.activities.length > 0;
    return hasChildFolders || hasActivities;
  }, [folder]);

  const getChildrenCount = useCallback(() => {
    if (!folder) return { folders: 0, activities: 0 };
    return {
      folders: folder.children ? folder.children.length : 0,
      activities: folder.activities ? folder.activities.length : 0
    };
  }, [folder]);

  return {
    deleteFolder,
    isDeleting,
    error,
    resetError,
    folderTitle: folder?.title || 'Unknown Folder',
    canDelete: !!(folder?.id && trip?.id),
    hasChildren: hasChildren(),
    childrenCount: getChildrenCount()
  };
};
